import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  TableContainer,
  Flex,
  Badge,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Collapse,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import { usePedidoSpot } from '../hooks/PedidoSpotContext'
import { LinhaPedidoSpot } from './LinhaPedidoSpot'
import { FiFilter, FiChevronDown, FiChevronUp, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi'

export const TabelaPedidosSpot = () => {
  const {
    pedidos = [],
    isLoading,
    error,
    isRefetching,
    filtros,
    aplicarFiltros,
    excluirPedido,
    pedidoParaExcluir,
    setPedidoParaExcluir,
    refetch,
  } = usePedidoSpot()
  const [searchParams, setSearchParams] = useSearchParams()

  const temFiltrosURL = searchParams.toString().length > 0
  const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: temFiltrosURL })
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()

  const [filtrosLocais, setFiltrosLocais] = useState({
    data_inicial: searchParams.get('data_inicial') || '',
    data_final: searchParams.get('data_final') || '',
    numero_pedido_innovation: searchParams.get('numero_pedido_innovation') || '',
    numero_pedido_spot: searchParams.get('numero_pedido_spot') || '',
    status: searchParams.get('status') || '',
  })

  const [isCancelando, setIsCancelando] = useState(false)
  const [isAtualizando, setIsAtualizando] = useState(false)

  useEffect(() => {
    if (temFiltrosURL) {
      aplicarFiltros(filtrosLocais)
      onOpen()
    }
  }, [])

  const handleFiltroChange = (e) => {
    const { name, value } = e.target
    setFiltrosLocais({
      ...filtrosLocais,
      [name]: value,
    })
  }

  const handleAplicarFiltros = () => {
    const params = new URLSearchParams()

    Object.entries(filtrosLocais).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      }
    })

    setSearchParams(params)
    aplicarFiltros(filtrosLocais)
  }

  const handleLimparFiltros = () => {
    const filtrosVazios = {
      data_inicial: '',
      data_final: '',
      numero_pedido_innovation: '',
      numero_pedido_spot: '',
      status: '',
    }

    setFiltrosLocais(filtrosVazios)
    setSearchParams({})
    aplicarFiltros(filtrosVazios)
  }

  const handleExcluirPedido = (pedido) => {
    setPedidoParaExcluir(pedido)
    onConfirmOpen()
  }

  const confirmarExclusao = async () => {
    if (pedidoParaExcluir) {
      setIsCancelando(true)
      try {
        await excluirPedido(pedidoParaExcluir.id, pedidoParaExcluir.numero_pedido_spot)
      } finally {
        setIsCancelando(false)
        setPedidoParaExcluir(null)
        onConfirmClose()
      }
    }
  }

  const handleAtualizar = async () => {
    setIsAtualizando(true)
    try {
      await refetch()
    } finally {
      setIsAtualizando(false)
    }
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Carregando pedidos SPOT...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>Erro ao carregar os pedidos SPOT. Por favor, tente novamente.</Text>
      </Alert>
    )
  }

  return (
    <Box>
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={2}>
          <Button onClick={onToggle} variant="outline" size="sm" leftIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}>
            {isOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>

          <Text fontWeight="medium">Total de pedidos: {pedidos.length}</Text>
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <Box width="100%" p={4} borderWidth="1px" borderRadius="lg" bg="white" mt={2}>
            <Text fontWeight="bold" mb={4}>
              Filtros
            </Text>

            <HStack spacing={4} flexWrap="wrap">
              <FormControl w="full" maxW="200px">
                <FormLabel>Data Inicial</FormLabel>
                <Input name="data_inicial" type="date" value={filtrosLocais.data_inicial} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="200px">
                <FormLabel>Data Final</FormLabel>
                <Input name="data_final" type="date" value={filtrosLocais.data_final} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="200px">
                <FormLabel>Nº Pedido Innovation</FormLabel>
                <Input
                  name="numero_pedido_innovation"
                  placeholder="Ex: 177777"
                  value={filtrosLocais.numero_pedido_innovation}
                  onChange={handleFiltroChange}
                />
              </FormControl>

              <FormControl w="full" maxW="200px">
                <FormLabel>Nº Pedido SPOT</FormLabel>
                <Input
                  name="numero_pedido_spot"
                  placeholder="Ex: ws0042320133894739005771513"
                  value={filtrosLocais.numero_pedido_spot}
                  onChange={handleFiltroChange}
                />
              </FormControl>

              <FormControl w="full" maxW="150px">
                <FormLabel>Status</FormLabel>
                <Select name="status" value={filtrosLocais.status} onChange={handleFiltroChange} placeholder="Todos">
                  <option value="c">Cancelados</option>
                </Select>
              </FormControl>

              <Flex alignSelf="flex-end" flexDir="row" alignItems="center" gap={2} spacing={2}>
                <Button leftIcon={<FiFilter />} colorScheme="blue" onClick={handleAplicarFiltros} width="full">
                  Filtrar
                </Button>
                <Button variant="outline" onClick={handleLimparFiltros} width="full">
                  Limpar
                </Button>
              </Flex>
            </HStack>

            {Object.values(filtros).some((f) => f) && (
              <HStack mt={4} spacing={2} flexWrap="wrap">
                <Text fontWeight="medium">Filtros aplicados:</Text>
                {filtros.data_inicial && (
                  <Badge colorScheme="green" p={1}>
                    A partir de: {new Date(filtros.data_inicial).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
                {filtros.data_final && (
                  <Badge colorScheme="green" p={1}>
                    Até: {new Date(filtros.data_final).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
                {filtros.numero_pedido_innovation && (
                  <Badge colorScheme="blue" p={1}>
                    Pedido Innovation: {filtros.numero_pedido_innovation}
                  </Badge>
                )}
                {filtros.numero_pedido_spot && (
                  <Badge colorScheme="purple" p={1}>
                    Pedido SPOT: {filtros.numero_pedido_spot}
                  </Badge>
                )}
                {filtros.status === 'c' && (
                  <Badge colorScheme="red" p={1}>
                    Status: Cancelados
                  </Badge>
                )}
              </HStack>
            )}
          </Box>
        </Collapse>
      </Box>

      <Flex justify="flex-end" align="center" mb={4}>
        {isRefetching && (
          <Badge display="flex" alignItems="center" colorScheme="blue" p={2} borderRadius="md">
            <Spinner size="sm" mr={2} />
            <Text fontSize="sm">Atualizando dados...</Text>
          </Badge>
        )}
      </Flex>

      <TableContainer>
        <Table variant="striped" size="md" position="relative">
          <Thead bg="#378BBF">
            <Tr>
              <Th color="white" borderBottom="none" borderRight="none">
                Data
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Pedido SPOT
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Pedido Innovation
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Responsável
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Empresa
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Fornecedor
              </Th>
              <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                Valor Total
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Status
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Ações
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {pedidos.length > 0 ? (
              pedidos.map((pedido) => <LinhaPedidoSpot key={pedido.id} pedido={pedido} onExcluir={() => handleExcluirPedido(pedido)} />)
            ) : (
              <Tr>
                <td colSpan={9}>
                  <Text textAlign="center" py={4}>
                    Nenhum pedido SPOT encontrado
                  </Text>
                </td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} closeOnOverlayClick={!isCancelando} closeOnEsc={!isCancelando}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmar Exclusão</ModalHeader>
          <ModalCloseButton isDisabled={isCancelando} />
          <ModalBody>
            <Flex direction="column" align="center">
              <FiAlertTriangle size={50} color="red" style={{ marginBottom: '16px' }} />
              <Text fontWeight="bold">Tem certeza que deseja cancelar este pedido?</Text>
              {pedidoParaExcluir && (
                <Text mt={2}>
                  Pedido SPOT: <strong>{pedidoParaExcluir.numero_pedido_spot}</strong>
                </Text>
              )}
              <Text mt={4} fontSize="sm" color="gray.600">
                Esta ação não poderá ser desfeita. O pedido será cancelado no fornecedor e seu status será atualizado para "cancelado".
              </Text>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onConfirmClose} isDisabled={isCancelando}>
              Cancelar
            </Button>
            <Button colorScheme="red" onClick={confirmarExclusao} isLoading={isCancelando} loadingText="Cancelando...">
              Confirmar Cancelamento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
